import React, { useEffect } from 'react';
import './Modal.css';

/**
 * Modal - Reusable modal/dialog component
 * Provides a consistent modal experience across the application
 *
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} title - Modal title
 * @param {React.ReactNode} children - Modal content
 * @param {Array} actions - Array of action buttons: [{ label: 'Cancel', onClick: fn, variant: 'secondary' }, ...]
 * @param {string} size - Modal size: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {boolean} closeOnOverlayClick - Whether clicking overlay closes modal (default: true)
 * @param {boolean} showCloseButton - Whether to show X close button (default: true)
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  actions = [],
  size = 'md',
  closeOnOverlayClick = true,
  showCloseButton = true
}) => {
  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden'; // Prevent background scrolling

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className={`modal-content modal-${size}`}>
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title">{title}</h3>
          {showCloseButton && (
            <button
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          )}
        </div>

        {/* Body */}
        <div className="modal-body">
          {children}
        </div>

        {/* Actions */}
        {actions.length > 0 && (
          <div className="modal-actions">
            {actions.map((action, index) => (
              <button
                key={index}
                className={`btn ${action.variant ? `btn-${action.variant}` : 'btn-primary'}`}
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
